import { CREDIT_ERROR_TIPS } from 'constants/errors/credit';
import { apiClient } from 'lib/apiClient';
import {
  convertErrorToErrorDescription,
  isArcApiError,
} from 'src/services/errors';
import {
  OnErrorCallback,
  OnSuccessInstantPaymentCallback,
} from 'src/services/orders/pay';
import { OrderDetail } from 'types/resources/orderDetail';

export type PayByApplePayInput = {
  domainName: string;
  label: string;
  userToken: string;
  orderId: OrderDetail['id'];
  applePaySession: ApplePaySession;
  onErrorCallback: OnErrorCallback;
  onSuccessCallback: OnSuccessInstantPaymentCallback;
};

export const payByApplePay = async (input: PayByApplePayInput) => {
  const {
    domainName,
    label,
    userToken,
    applePaySession,
    orderId,
    onErrorCallback,
    onSuccessCallback,
  } = input;

  applePaySession.begin();

  applePaySession.onvalidatemerchant = (
    event: ApplePayJS.ApplePayValidateMerchantEvent
  ): void => {
    validateMerchant(event.validationURL, userToken, domainName, label).then(
      async (merchantSession) => {
        applePaySession.completeMerchantValidation(merchantSession);
      }
    );
  };

  applePaySession.oncancel = async (event: ApplePayJS.Event): Promise<void> => {
    try {
      console.info('on apple pay cancel', event);
      onErrorCallback('購入を中止しました');
    } catch (e) {
      console.error('on apple pay cancel error', e);
      onErrorCallback('注文に失敗しました');
      applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
    }
  };

  applePaySession.onpaymentauthorized = async (
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent
  ): Promise<void> => {
    try {
      const paymentToken = event.payment.token.paymentData;
      const base64_token =
        process.env.NEXT_PUBLIC_APPLE_PAY_TEST_TOKEN ??
        Buffer.from(JSON.stringify(paymentToken)).toString('base64');

      const buyRes = await apiClient.v1.orders
        ._orderId(orderId)
        .payments.applepay.post({
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
          body: {
            token: base64_token,
          },
        });
      if (buyRes.status == 200) {
        applePaySession.completePayment(ApplePaySession.STATUS_SUCCESS);
        onSuccessCallback(orderId);
      } else {
        if (isArcApiError(buyRes.body)) {
          const errorTips = CREDIT_ERROR_TIPS[buyRes.body.code];
          onErrorCallback(
            'ApplePayでの購入に失敗しました',
            buyRes.body.message,
            errorTips ?? ''
          );
          applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
          return;
        }
        throw new Error('ApplePayでの購入に失敗しました');
      }
    } catch (e) {
      applePaySession.completePayment(ApplePaySession.STATUS_FAILURE);
      const error = convertErrorToErrorDescription(
        e,
        'ApplePayでの購入に失敗しました'
      );
      onErrorCallback('ApplePayでの購入に失敗しました', error.message);
      throw error;
    }
  };
};

const validateMerchant = async (
  validationURL: string,
  userToken: string,
  domainName: string,
  label: string
) => {
  try {
    const end_point_url = `${validationURL}`;

    const response = await apiClient.v1.payments.apple_pay_session.post({
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      body: {
        sessionUrl: end_point_url,
        initiativeContext: domainName,
        displayName: label,
      },
    });
    console.log('validateMerchant response', response);
    return response.body;
  } catch (e) {
    console.error('validateMerchant', e);
  }
};
